// LearnCore Frontend - 学习时间追踪 Hook

import { useEffect, useRef, useState } from 'react';
import { apiClient } from '../api/client';
import type { StudyMode } from '../types';

interface UseStudyTimeTrackerOptions {
  setId: string;
  mode: StudyMode;
  enabled?: boolean;
  inactivityThreshold?: number; // 非活跃判定阈值（毫秒），默认2分钟
  updateInterval?: number; // 更新间隔（毫秒），默认30秒
}

interface StudyTimeTrackerState {
  isTracking: boolean;
  activeDuration: number; // 活跃学习时长（秒）
  totalDuration: number; // 总时长（秒）
  cardsStudied: number;
  interactions: number;
}

export function useStudyTimeTracker(options: UseStudyTimeTrackerOptions) {
  const {
    setId,
    mode,
    enabled = true,
    inactivityThreshold = 120000, // 2分钟
    updateInterval = 30000 // 30秒
  } = options;

  const [state, setState] = useState<StudyTimeTrackerState>({
    isTracking: false,
    activeDuration: 0,
    totalDuration: 0,
    cardsStudied: 0,
    interactions: 0
  });

  const logIdRef = useRef<string | null>(null);
  const sessionStartTimeRef = useRef<number>(0);
  const lastActivityTimeRef = useRef<number>(Date.now());
  const lastUpdateTimeRef = useRef<number>(0);
  const updateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const accumulatedActiveTimeRef = useRef<number>(0);
  const wasInactiveRef = useRef<boolean>(false);
  
  // 使用 ref 来存储 cards 和 interactions，避免 state 异步更新问题
  const cardsStudiedRef = useRef<number>(0);
  const interactionsRef = useRef<number>(0);

  // 记录用户活动
  const recordActivity = () => {
    lastActivityTimeRef.current = Date.now();
    
    // 如果之前是非活跃状态，现在恢复活跃
    if (wasInactiveRef.current) {
      wasInactiveRef.current = false;
    }
  };

  // 记录学习行为（如翻卡片、答题等）
  const recordInteraction = (cardsIncrement: number = 0) => {
    // 使用 ref 立即更新，避免异步问题
    cardsStudiedRef.current += cardsIncrement;
    interactionsRef.current += 1;
    
    // 同步更新 state 用于 UI 显示
    setState(prev => ({
      ...prev,
      interactions: interactionsRef.current,
      cardsStudied: cardsStudiedRef.current
    }));
    recordActivity();
  };

  // 计算当前的活跃时长
  const calculateDurations = () => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityTimeRef.current;
    const timeSinceStart = now - sessionStartTimeRef.current;
    const totalSeconds = Math.floor(timeSinceStart / 1000);

    // 判断当前是否活跃
    const isCurrentlyActive = timeSinceLastActivity < inactivityThreshold;

    let activeSeconds = Math.floor(accumulatedActiveTimeRef.current / 1000);

    // 如果当前活跃，累加自上次更新以来的活跃时间
    if (isCurrentlyActive) {
      const timeSinceLastUpdate = now - lastUpdateTimeRef.current;
      // 只累加活跃时间段，避免过度累积
      const activeTimeSinceLastUpdate = Math.min(timeSinceLastUpdate, timeSinceLastActivity);
      activeSeconds += Math.floor(activeTimeSinceLastUpdate / 1000);
    } else {
      // 如果当前不活跃但之前是活跃的，只累加到非活跃前的时间
      if (!wasInactiveRef.current) {
        const activeTimeSinceLastUpdate = Math.min(
          timeSinceLastActivity,
          now - lastUpdateTimeRef.current
        );
        activeSeconds += Math.floor(activeTimeSinceLastUpdate / 1000);
        wasInactiveRef.current = true;
      }
      // 当前不活跃，不累加任何时间
    }

    return {
      activeSeconds,
      totalSeconds
    };
  };

  // 更新服务器端的学习时间
  const updateServerTime = async () => {
    if (!logIdRef.current || !state.isTracking) return;

    const { activeSeconds, totalSeconds } = calculateDurations();

    try {
      // 使用 ref 的值，确保是最新的
      await apiClient.updateStudyTime({
        log_id: logIdRef.current,
        active_duration: activeSeconds,
        total_duration: totalSeconds,
        cards_studied: cardsStudiedRef.current,
        interactions: interactionsRef.current
      });

      // 更新累计时间（毫秒）和上次更新时间
      accumulatedActiveTimeRef.current = activeSeconds * 1000;
      lastUpdateTimeRef.current = Date.now();

      // 更新本地状态
      setState(prev => ({
        ...prev,
        activeDuration: activeSeconds,
        totalDuration: totalSeconds
      }));

    } catch (error) {
      console.error('更新学习时间失败:', error);
    }
  };

  // 开始追踪
  const startTracking = async () => {
    if (state.isTracking || !enabled) return;

    try {
      // 使用秒级时间戳与后端保持一致
      const startTime = Math.floor(Date.now() / 1000);
      const response = await apiClient.startStudyTime({
        set_id: setId,
        mode,
        start_time: startTime
      });

      logIdRef.current = response.log_id;
      // 记录毫秒级开始时间，用于精确计算
      sessionStartTimeRef.current = Date.now();
      lastActivityTimeRef.current = Date.now();
      lastUpdateTimeRef.current = Date.now();
      accumulatedActiveTimeRef.current = 0;
      wasInactiveRef.current = false;

      // 重置 ref 计数器
      cardsStudiedRef.current = 0;
      interactionsRef.current = 0;

      setState({
        isTracking: true,
        activeDuration: 0,
        totalDuration: 0,
        cardsStudied: 0,
        interactions: 0
      });

      // 设置定期更新
      updateIntervalRef.current = setInterval(updateServerTime, updateInterval);

      console.log('[StudyTimeTracker] 开始追踪学习时间:', response.log_id);
    } catch (error) {
      console.error('开始追踪学习时间失败:', error);
    }
  };

  // 停止追踪
  const stopTracking = async () => {
    if (!state.isTracking || !logIdRef.current) return;

    // 清除定时器
    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
      updateIntervalRef.current = null;
    }

    const { activeSeconds, totalSeconds } = calculateDurations();

    try {
      // 确保活跃时间不超过总时间
      const finalActiveSeconds = Math.min(activeSeconds, totalSeconds);

      // 使用 ref 的值，确保是最新的
      await apiClient.endStudyTime({
        log_id: logIdRef.current,
        end_time: Math.floor(Date.now() / 1000),
        active_duration: finalActiveSeconds,
        total_duration: totalSeconds,
        cards_studied: cardsStudiedRef.current,
        interactions: interactionsRef.current
      });

      console.log('[StudyTimeTracker] 停止追踪学习时间');
    } catch (error) {
      console.error('停止追踪学习时间失败:', error);
    }

    setState(prev => ({ ...prev, isTracking: false }));
    logIdRef.current = null;
  };

  // 页面可见性变化处理
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // 页面隐藏时，更新一次服务器时间
        updateServerTime();
      } else {
        // 页面恢复可见时，记录活动
        recordActivity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [state.isTracking]);

  // 监听用户活动
  useEffect(() => {
    if (!enabled || !state.isTracking) return;

    const handleUserActivity = () => recordActivity();

    // 监听各种用户活动事件
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('mousedown', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity);
    window.addEventListener('touchstart', handleUserActivity);

    return () => {
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('mousedown', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
      window.removeEventListener('touchstart', handleUserActivity);
    };
  }, [enabled, state.isTracking]);

  // 组件卸载时停止追踪
  useEffect(() => {
    if (enabled) {
      startTracking();
    }

    return () => {
      stopTracking();
    };
  }, [setId, mode, enabled]);

  return {
    ...state,
    recordInteraction,
    recordActivity
  };
}
